/**
 * Display-only money formatting shared by the service editor's live preview and the public pay
 * page (/pay/:token): "RD$ 1,000" for DOP, "US$ 1,000" for USD — the symbols the editor's pricing
 * section already shows. Never used to build a request: prices are the server's (CLAUDE.md).
 *
 * - Whole amounts have no decimals ("RD$ 1,000"); anything else shows two ("US$ 85.50").
 * - An unknown ISO code is shown as the code ("EUR 10"); no currency at all falls back to a bare
 *   "$" — never invent one.
 */
export const CURRENCY_SYMBOL: Readonly<Record<string, string>> = { DOP: 'RD$', USD: 'US$' };

/** "RD$" / "US$" for a known ISO code, the code itself otherwise, "$" when there is none. */
export function currencySymbol(currency: string | null | undefined): string {
  const code = String(currency ?? '').trim().toUpperCase();
  if (!code) return '$';
  return CURRENCY_SYMBOL[code] || code;
}

/** '' for a missing or non-numeric amount. `locale` only changes the digit grouping. */
export function formatMoneyAmount(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  locale = 'en-US',
): string {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = typeof amount === 'number' ? amount : Number(String(amount).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  const whole = Math.abs(abs - Math.round(abs)) < 0.005;
  const digits = new Intl.NumberFormat(locale, {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(whole ? Math.round(abs) : abs);
  return `${n < 0 ? '-' : ''}${currencySymbol(currency)} ${digits}`;
}

/**
 * The admin's payments (Payment Details, the Transactions table, the refund dialog, Activity): the same symbol as
 * above and ALWAYS two decimals, "RD$ 2.00" — a charge of 2 pesos is never "RD$ 2" in one screen and "$2.00 DOP" in
 * another. '' for a missing or non-numeric amount.
 */
export function formatMoneyFixed(
  amount: number | string | null | undefined,
  currency: string | null | undefined = 'DOP',
  locale = 'en-US',
): string {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = typeof amount === 'number' ? amount : Number(String(amount).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '';
  const digits = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));
  return `${n < 0 ? '-' : ''}${currencySymbol(currency)} ${digits}`;
}
